import { Link } from "react-router-dom";

import type { Id, TreeProgress } from "../../../api/types";
import { useApiQuery } from "../../../api/hooks";
import { Loading, ProgressRing, StateBadge } from "../../../ui";
import { useSeen } from "../hooks";
import { progressWords, type NavNode } from "../model/nav";
import { navigatorHref } from "../model/words";
import { ItemMiniMap } from "./ItemMiniMap";

interface ItemTileProps {
  node: NavNode;
  progress: TreeProgress | null;
  /** The library's calm hue, 1 to 6. */
  hue: number;
  /** The path of headings above the item, as one line. */
  path: string;
}

/**
 * One tile of the gallery: the item's name, its ring (validated and waiting for the SME), its state, and a
 * small map. The map is read only when the tile comes near the screen, so a gallery of hundreds of items asks
 * the server for the few that are in view.
 */
export function ItemTile({ node, progress, hue, path }: ItemTileProps) {
  const [ref, seen] = useSeen<HTMLLIElement>();
  const snapshot = useApiQuery("item_snapshot", { id: node.id as Id }, { enabled: seen, staleTime: 30_000 });
  const notDrawn = node.abstract || !node.drawn;
  return (
    <li ref={ref} className="nv-gtile" data-hue={hue} data-state={node.state ?? undefined}>
      <div className="nv-gtile__head">
        <ProgressRing validated={progress?.validated ?? 0} waiting={progress?.waiting ?? 0} total={progress?.total ?? 0} size={44} />
        <div className="nv-gtile__title">
          <Link to={`/items/${encodeURIComponent(node.id)}/map`} className="nv-gtile__name">
            {node.label}
          </Link>
          <span className="nv-gtile__path muted">{path || "No family yet"}</span>
        </div>
      </div>
      <div className="nv-gtile__map">
        {snapshot.data ? (
          <ItemMiniMap name={node.label} snapshot={snapshot.data} />
        ) : snapshot.isError ? (
          <p className="muted nv-gtile__none">No map to show yet.</p>
        ) : seen ? (
          <Loading>Reading the map</Loading>
        ) : (
          <div className="nv-gtile__placeholder" aria-hidden="true" />
        )}
      </div>
      <div className="nv-gtile__foot">
        {node.state && <StateBadge state={node.state} />}
        {notDrawn && <span className="muted">Not drawn</span>}
        <span className="muted nv-gtile__progress">{progressWords(progress)}</span>
      </div>
      <div className="nv-gtile__links">
        <Link to={`/items/${encodeURIComponent(node.id)}`}>Item page</Link>
        <Link to={navigatorHref(node.id)}>Show in the tree</Link>
      </div>
    </li>
  );
}
